import { Widget } from "@foretagsplatsen/widgetjs";
import ReportTableWidget from "../ReportTableWidget/ReportTableWidget.js";
import ReportViewerRowTitleRenderer from "./ReportViewerRowTitleRenderer/ReportViewerRowTitleRenderer.js";

/**
 * Simple viewer for reports.
 *
 * @param {report} [spec.report] - Report to display
 * @param {function} [spec.getCellContentClickedCallback] - Called with a
 * {row,column} argument and is expected to return a function if the
 * cell's content is clickable and a falsy value if not. If a function is
 * returned, this function will be called if the cell's content is clicked. If
 * this argument is not provided, clicking a cell's content won't do anything.
 * This is mutually exclusive with `getOnCellClickCallback`.
 * @param {Renderable} [titleCard] - A widget to render in the top-left-hand corner of
 *   the report
 * @param {function} onAccountStatementRedirectionRequest - Will be
 *   called with an account number and a period and should open the
 *   account statement for this account number at this period
 * @param {Renderer} [budgetNameRenderer] - Takes a budget name as
 *   argument and should return a renderable for it
 *  @param {Renderer} [estimateNameRenderer] - Takes a ForecastEstimate id as
 *   argument and should return a renderable for it. By default, it returns
 *   the id.
 */
export default class ReportViewerWidget extends Widget {
	constructor({
		report,
		getCellContentClickedCallback,
		cssClass = "sticky-first-row",
		onAccountStatementRedirectionRequest,
		budgetNameRenderer,
		estimateNameRenderer,
	} = {}) {
		super(...arguments);

		this._getCellContentClickedCallback = getCellContentClickedCallback;
		this._cssClass = cssClass;

		this._onAccountStatementRedirectionRequest =
			onAccountStatementRedirectionRequest;

		this._budgetNameRenderer = budgetNameRenderer;
		this._estimateNameRenderer = estimateNameRenderer;

		this.setReport(report);
	}

	getReport() {
		return this._report;
	}

	setReport(newReport) {
		this._report = newReport;
		this._reportWidget = newReport && this._makeReportRenderer();
	}

	renderContentOn(html) {
		html.div(
			{
				klass: ["reportViewer", this._cssClass],
				"data-test": "report-viewer",
			},
			this._reportWidget,
		);
	}

	_makeReportRenderer() {
		return new ReportTableWidget({
			report: this._report,
			makeRowTitleRenderer: this._makeRowTitleRenderer.bind(this),
			dataCellRenderer: this._dataCellRenderer.bind(this),
			budgetNameRenderer: this._budgetNameRenderer,
			estimateNameRenderer: this._estimateNameRenderer,
			showReportTitle: false,
		});
	}

	_makeRowTitleRenderer(spec) {
		const visitor = new ReportViewerRowTitleRenderer({
			...spec,
			onAccountStatementRedirectionRequest:
				this._onAccountStatementRedirectionRequest,
		});

		return (row) => visitor.visit(row);
	}

	_dataCellRenderer({ row, column, cssClass, content }) {
		return (html) => {
			html.td(
				{
					class: cssClass,
					"data-row-id": row.getId(),
					"data-column-id": column.getId(),
				},
				this._cellDataRenderer({ row, column, content }),
			);
		};
	}

	_cellDataRenderer({ row, column, content = "" }) {
		const click = this._getCellContentClickedCallback?.({
			row,
			column,
		});

		return (html) =>
			click ? html.a({ click }, content) : html.span(content);
	}
}
